<script setup lang="ts">
import { computed } from 'vue';

import type { ModuleRecord } from '@/types/domain';
import { statusText } from '@/utils/format';
import {
  dashboardConfig,
  dueDate,
  isOverdue,
  isTerminal,
  ownerName,
  projectLabel,
  type DashboardDimension,
  type DashboardLabels,
  type DashboardScope,
} from '@/utils/module-dashboard';

const props = withDefaults(defineProps<{
  moduleKey: string;
  entityName: string;
  items: ModuleRecord[];
  /** 在某个项目里打开时传 project：跨项目的维度和 KPI 会被摘掉 */
  scope?: DashboardScope;
  /** 项目 id →「项目号 · 名称」，和清单「关联项目」列同一份（1027 复评 r2） */
  projectLabels?: Record<string, string>;
  /** 变更类型 ID → 中文名（form-options 的 changeType 字典） */
  changeTypeLabels?: Record<string, string>;
}>(), { scope: 'global' });

const emit = defineEmits<{
  drill: [filter: { kind: string; value: string }];
}>();

interface Slice { label: string; value: string; count: number; tone?: 'risk' | 'warning' | 'normal' }
interface Kpi { label: string; value: string | number; tone?: 'risk' | 'warning'; hint?: string }

const config = computed(() => dashboardConfig(props.moduleKey, props.scope));

/** 画图借的字典；列表下钻（matchesDrill）要传同一份，柱子才筛得回同样那几行 */
const labels = computed<DashboardLabels>(() => ({
  projects: props.projectLabels,
  changeTypes: props.changeTypeLabels,
}));

function tally(dimension: DashboardDimension): Slice[] {
  const map = new Map<string, number>();
  for (const item of props.items) {
    /*
      read 给空串 = 这条不进这张图（1028 Soft-Gate P2）。原来兜成「未填写」：
      积压图把已结束的改成空串之后，会冒出一根「未填写」，和「只统计未结束的记录」照样对不上。
      其余维度的 read 都自带「未填…」兜底，不会给空串；下钻（matchesDrill）同样筛不到这些行。
    */
    const label = dimension.read(item, labels.value);
    if (!label) continue;
    map.set(label, (map.get(label) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([label, count]): Slice => ({
      label,
      value: label,
      count,
      tone: /^未|无提出日期|超过 60 天/u.test(label) ? 'warning' : undefined,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, dimension.limit ?? 10);
}

const openCount = computed(() => props.items.filter((item) => !isTerminal(item.status)).length);
const overdue = computed(() => props.items.filter(isOverdue).length);
const unlinked = computed(() =>
  props.items.filter((item) => projectLabel(item, props.projectLabels) === '未关联项目').length);
const unowned = computed(() =>
  props.items.filter((item) => ownerName(item) === '未指定').length);
const noDue = computed(() =>
  props.items.filter((item) => !isTerminal(item.status) && !dueDate(item)).length);

/** 模块有没有「挂项目」这个概念——人员、配置没有，不该给个假的 0 */
const projectScoped = computed(() => props.items.some((item) =>
  item.project_id !== undefined || item.projectId !== undefined));

const kpis = computed<Kpi[]>(() => {
  const list: Kpi[] = [
    { label: `${props.entityName}总数`, value: props.items.length },
    /*
      1028 Soft-Gate copy-tail（复评 r2 步骤 10）：模块中心的变更页顶上有「待处理」（不含已退回），
      这里多出的正是已退回那几条——退回不是终态，还要重填往下走。只靠悬停复评仍读不出差在哪，
      眉标直接写「含退回」，和顶部「待处理·不含退回」成对。项目内打开时顶上没有「待处理」，不加这一笔。
    */
    props.moduleKey === 'changes' && props.scope === 'global'
      ? { label: '未结束·含退回', value: openCount.value, hint: '含已退回；顶部「待处理」不含已退回' }
      : { label: '未结束', value: openCount.value },
  ];
  if (config.value?.tracksDue) {
    if (overdue.value) list.push({ label: '已逾期', value: overdue.value, tone: 'risk' });
    if (noDue.value) {
      list.push({
        label: '未设完成日期',
        value: noDue.value,
        tone: 'warning',
        hint: '没有到期日就排不进催办，也算不出逾期',
      });
    }
  }
  // 「未关联项目」在某个项目里打开时永远是 0，不出这个 KPI
  if (props.scope === 'global' && projectScoped.value && unlinked.value) {
    list.push({ label: '未关联项目', value: unlinked.value, tone: 'risk' });
  }
  if (unowned.value && config.value?.ownerKpi) {
    list.push({ label: config.value.ownerKpi, value: unowned.value, tone: 'warning' });
  }
  return list;
});

/**
 * 状态分布按**原始值**分桶，不按显示名。
 *
 * statusText 会把 Released 和 released 都翻成「已发布」；按显示名分桶的话
 * 图上是一根 2 条的柱子，点进去却只筛出 1 条——因为下钻走的是原始值。
 * 所以这里一个原始值一根柱子，两个原始值撞同一个中文名时把原文缀在后面。
 */
const byStatus = computed<Slice[]>(() => {
  const counts = new Map<string, number>();
  for (const item of props.items) {
    const raw = String(item.status ?? '');
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }
  const labelUse = new Map<string, number>();
  for (const raw of counts.keys()) {
    const label = statusText(raw) || '未填状态';
    labelUse.set(label, (labelUse.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([raw, count]): Slice => {
      const label = statusText(raw) || '未填状态';
      return {
        label: (labelUse.get(label) ?? 0) > 1 ? `${label}（${raw}）` : label,
        value: raw,
        count,
        tone: isTerminal(raw) ? 'normal' : 'warning',
      };
    })
    .sort((a, b) => b.count - a.count);
});

const charts = computed(() => [
  { kind: 'status', title: '按状态', note: undefined as string | undefined, slices: byStatus.value },
  ...(config.value?.dimensions ?? []).map((dimension) => ({
    kind: dimension.kind,
    title: dimension.title,
    note: dimension.note,
    slices: tally(dimension),
  })),
]);

/**
 * 状态取值本身的质量问题。
 *
 * 文件管理里同时存在 Released / Draft / active / legacy_orphan——
 * 大小写和中英混用，统计时会被当成不同状态。这不是显示问题，是数据问题，
 * 摆出来比在前端悄悄合并更有用：合并了就没人去修库里的值。
 */
const statusHygiene = computed(() => {
  const seen = new Map<string, Set<string>>();
  for (const item of props.items) {
    const raw = String(item.status ?? '').trim();
    if (!raw) continue;
    const set = seen.get(raw.toLowerCase()) ?? new Set<string>();
    set.add(raw);
    seen.set(raw.toLowerCase(), set);
  }
  const collisions = [...seen.values()].filter((set) => set.size > 1).map((set) => [...set].join(' / '));
  const chinese = [...seen.values()].some((set) => [...set].some((value) => /[一-龥]/u.test(value)));
  const english = [...seen.values()].some((set) => [...set].every((value) => /^[a-z_]+$/iu.test(value)));
  return { collisions, mixedLanguage: chinese && english };
});

const maxOf = (slices: Slice[]): number => Math.max(1, ...slices.map((item) => item.count));
</script>

<template>
  <section v-if="items.length" class="module-dashboard" aria-label="模块状态看板">
    <div class="md-kpis">
      <article v-for="kpi in kpis" :key="kpi.label" :class="kpi.tone" :title="kpi.hint">
        <b>{{ kpi.value }}</b><span>{{ kpi.label }}</span>
      </article>
    </div>

    <p v-if="statusHygiene.collisions.length || statusHygiene.mixedLanguage" class="md-hygiene">
      状态取值不统一，统计时会被拆成不同的状态：
      <template v-if="statusHygiene.collisions.length">大小写不一致（{{ statusHygiene.collisions.join('；') }}）；</template>
      <template v-if="statusHygiene.mixedLanguage">中文与英文状态并存。</template>
    </p>

    <div class="md-charts">
      <article v-for="chart in charts" :key="chart.kind">
        <h4>{{ chart.title }}</h4>
        <p v-if="chart.note" class="md-note">{{ chart.note }}</p>
        <p v-if="!chart.slices.length" class="md-note">暂无数据</p>
        <ul v-else>
          <li v-for="slice in chart.slices" :key="slice.value">
            <button type="button" @click="emit('drill', { kind: chart.kind, value: slice.value })">
              <span class="md-label" :title="slice.label">{{ slice.label }}</span>
              <span class="md-track">
                <i
                  :class="slice.tone"
                  :style="{ width: `${Math.round((slice.count / maxOf(chart.slices)) * 100)}%` }"
                ></i>
              </span>
              <b>{{ slice.count }}</b>
            </button>
          </li>
        </ul>
      </article>
    </div>
  </section>
</template>

<style scoped>
.module-dashboard { display: grid; gap: 12px; margin-bottom: 14px; }
.md-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
.md-kpis article { display: grid; gap: 2px; padding: 11px 13px; border: 1px solid var(--line); border-left: 3px solid var(--blue); background: var(--white); }
.md-kpis article.risk { border-left-color: var(--risk); }
.md-kpis article.warning { border-left-color: var(--warning-graphic); }
.md-kpis b { font: 700 24px var(--display); }
.md-kpis article.risk b { color: var(--risk); }
.md-kpis article.warning b { color: var(--warning); }
.md-kpis span { color: var(--muted); font-size: 12px; }

.md-hygiene { margin: 0; padding: 8px 11px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.8; }

.md-charts { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 12px; }
.md-charts article { padding: 12px 14px; border: 1px solid var(--line); background: var(--white); }
.md-charts h4 { margin: 0 0 6px; font-size: 13px; }
.md-note { margin: 0 0 8px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.md-charts ul { display: grid; gap: 5px; margin: 0; padding: 0; list-style: none; }
.md-charts button { width: 100%; display: grid; grid-template-columns: minmax(70px, 116px) 1fr 34px; gap: 8px; align-items: center; padding: 3px 4px; border: 0; background: transparent; font: inherit; text-align: left; cursor: pointer; }
.md-charts button:hover, .md-charts button:focus-visible { background: #EEF4FA; }
.md-label { overflow: hidden; color: var(--ink); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.md-track { height: 14px; background: var(--paper); }
.md-track i { display: block; height: 100%; min-width: 2px; background: var(--blue); }
.md-track i.risk { background: var(--risk); }
.md-track i.warning { background: var(--warning-graphic); }
.md-track i.normal { background: var(--green); }
.md-charts b { font-size: 12px; text-align: right; }
</style>
